import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ActionDownload from "assets/img/svg/actionDownload";
import Basket from "assets/img/svg/basket";
import DocumentEmptyState from "assets/img/svg/documentEmptyState";
import Pdf from "assets/img/svg/pdf";
import Plus from "assets/img/svg/plus";
import SendEmailIcon from "assets/img/svg/sendEmail";
import AddDocument from "components/shared/AddDocument";
import type { IDocumentTypeFieldsValue } from "components/shared/DocumentTypeFields";
import SendEmailBeneficiar from "components/shared/SendEmailBeneficiar";
import { toTermBeneficiary } from "components/shared/SendEmailBeneficiar/helpers";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { documentStatusRules, getDocumentRowStatus, listOutdatedDocuments } from "helpers/beneficiaryDocuments";
import { formatLocaleDateString } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendDocumentsReminder } from "store/beneficiaryDocumentsOverviewStore";
import type { IBeneficiaryDocumentType } from "store/beneficiaryDocumentTypesStore";
import type { IDocument } from "store/documentStore";
import { deleteDocument, updateDocument } from "store/documentStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import DeleteModal from "../DeleteModal";
import FilterInput from "../FilterInput";
import LastElementTable from "../LastElementTable";
import type { LastElementTableColumns } from "../LastElementTable/type";
import StatusChip from "../StatusChip";
import StyledButton from "../StyledButton";
import { getFileSize } from "./../../../helpers/utils";
import QualifyDocumentModal from "./QualifyDocumentModal";
import RejectDocumentModal from "./RejectDocumentModal";

interface DocumentsProps {
    documentList: any[];
    documentStatus: TFetchStatus;
    handleFileAdd: (file: { formData: FormData }) => void;
    handleFileDownload: (file: any) => void;
    hasSubtitles?: boolean;
    /**
     * Company document types (tracking enabled): adds the Type / Validity / Status columns,
     * the typed upload and the qualify / reject actions on documents uploaded by the author.
     */
    documentTypes?: IBeneficiaryDocumentType[];
    /** Beneficiary owning the documents; defaults to the beneficiary selected in the store. */
    beneficiaryId?: number;
}

/** Longest rejection reason shown in the status chip; the full text stays in its tooltip. */
const REJECTION_REASON_MAX_LENGTH = 40;

const Documents = (props: DocumentsProps) => {
    const {
        documentList,
        documentStatus,
        handleFileAdd,
        handleFileDownload,
        hasSubtitles = true,
        documentTypes,
        beneficiaryId,
    } = props;
    const [t] = useTranslation();
    const hasTracking = !!documentTypes;
    const statusRules = documentStatusRules(t);
    const dispatch = useAppDispatch();
    const [filterValue, setFilterValue] = useState("");
    const { user } = useAuthenticator((context) => [context.user]);
    const { beneficiar } = useAppSelector((state) => state.beneficiary);
    // Deactivated types are no longer offered but their documents keep their name
    const { documentTypes: allDocumentTypes } = useAppSelector((state) => state.beneficiaryDocumentTypes);
    const ownerId = beneficiaryId ?? beneficiar?.id ?? -1;
    const [documentToQualify, setDocumentToQualify] = useState<IDocument | undefined>();
    const [documentToReject, setDocumentToReject] = useState<IDocument | undefined>();

    const handleQualify = async (value: IDocumentTypeFieldsValue) => {
        if (!documentToQualify) return;
        const result = await dispatch(
            updateDocument({ beneficiarId: ownerId, documentId: documentToQualify.id, data: value }),
        );
        if (updateDocument.fulfilled.match(result)) setDocumentToQualify(undefined);
    };

    const handleReject = async (rejectionReason: string) => {
        if (!documentToReject) return;
        const result = await dispatch(
            updateDocument({
                beneficiarId: ownerId,
                documentId: documentToReject.id,
                data: { status: "rejected", rejectionReason },
            }),
        );
        if (updateDocument.fulfilled.match(result)) setDocumentToReject(undefined);
    };
    const userEmail = user.attributes ? user.attributes["email"] : "";
    // The list can hold empty entries (e.g. an upload resolved without a document in its response)
    const filteredArray = documentList.filter(
        (item: any) => !!item && (item.name ?? "").toLowerCase().includes(filterValue.toLowerCase()),
    );
    const pendingCount = filteredArray.filter((document: IDocument) => document.status === "pending").length;
    const isOwnerLoaded = hasTracking && beneficiar?.id === ownerId;
    const beneficiaryName = isOwnerLoaded
        ? `${beneficiar.surname?.toUpperCase() ?? ""} ${beneficiar.name ?? ""}`.trim()
        : undefined;
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    /** Type preselected in the upload modal when it is opened from a "missing" row */
    const [addDocumentTypeId, setAddDocumentTypeId] = useState<number | undefined>();
    const openAddModal = (documentTypeId?: number) => {
        setAddDocumentTypeId(documentTypeId);
        setIsAddModalOpen(true);
    };
    const [isReminderOpen, setIsReminderOpen] = useState(false);
    const ability = useAbility();
    const canRemind = isOwnerLoaded && ability.can("update", "Beneficiary");
    // Same rights as the backend routes: PATCH needs both, DELETE and POST their own subject
    const canQualify = ability.can("update", "Beneficiary") && ability.can("update", "BeneficiaryDocument");
    const canDelete = ability.can("delete", "BeneficiaryDocument");
    const canAdd = ability.can("create", "BeneficiaryDocument");
    /** Active types, plus the current one when it was deactivated since (edit validity) */
    const typesForQualify = (document?: IDocument) => {
        const active = documentTypes ?? [];
        const current = allDocumentTypes.find((type) => type.id === document?.documentTypeId);

        return current && !active.some((type) => type.id === current.id) ? [...active, current] : active;
    };

    const handleSendReminder = async ({
        subject,
        body,
        mailCc,
    }: {
        subject: string;
        body: string;
        mailCc: string[];
    }) => {
        await dispatch(sendDocumentsReminder({ beneficiaryIds: [ownerId], subject, body, mailCc }));
    };
    // Only once the list is loaded: while it is fetched (or after a failure) every type would look missing
    const outdatedDocuments = useMemo(
        () =>
            isOwnerLoaded && documentStatus === "succeeded"
                ? listOutdatedDocuments(documentTypes ?? [], beneficiar, documentList)
                : [],
        [isOwnerLoaded, documentStatus, documentTypes, beneficiar, documentList],
    );
    const reminderPreview = useMemo(
        () => (isOwnerLoaded ? { beneficiaryName: beneficiaryName ?? "", documents: outdatedDocuments } : undefined),
        [isOwnerLoaded, beneficiaryName, outdatedDocuments],
    );
    /**
     * One row per type expected from the beneficiary without any document (a pending upload of the type
     * already has its own "to qualify" row): ids are negative to never clash with real documents
     */
    const missingRows = outdatedDocuments
        .filter((outdated) => outdated.status === "missing")
        .filter(
            (outdated) =>
                !documentList.some(
                    (document: IDocument) =>
                        document.documentTypeId === outdated.typeId && document.status === "pending",
                ),
        )
        .filter((outdated) => outdated.typeName.toLowerCase().includes(filterValue.toLowerCase()))
        .map((outdated) => ({
            id: -outdated.typeId,
            name: outdated.typeName,
            documentTypeId: outdated.typeId,
            missing: true,
        }));
    const rows = [...missingRows, ...filteredArray];
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<IDocument | undefined>();

    const handleShowDocumentDeleteModal = (document?: IDocument) => {
        if (document == undefined || document.id == undefined) return;
        setIsDeleteModalOpen(true);
        setDocumentToDelete(document);
    };

    const handleDeleteDocument = async (isDeleting = true) => {
        if (isDeleting)
            await dispatch(deleteDocument({ beneficiarId: ownerId, documentId: documentToDelete?.id ?? -1 }));
        setIsDeleteModalOpen(false);
        setDocumentToDelete(undefined);
    };

    const download = (document: IDocument) => handleFileDownload({ file: document, beneficiaryId: ownerId });

    /** "Vous" for the current user, the author's name for uploads from the author space. */
    const uploadedByLabel = (value?: string) => {
        if (!value) return "-";
        if (value === userEmail) return t("BeneficiarInfo.You");
        if (isOwnerLoaded && value === beneficiar.email) {
            return `${beneficiaryName} ${t("BeneficiaryDocuments.Documents.FromAuthorSpace")}`;
        }

        return value;
    };
    const muted = (content: React.ReactNode, italic = false) => (
        <span className={`documents-muted${italic ? " documents-italic" : ""}`}>{content}</span>
    );
    // Planche 1 shows short numeric dates (01/01/2026); the legacy tab keeps the long ones
    const formatDate = (value?: string | null) =>
        value ? formatLocaleDateString({ date: value, options: { numeric: hasTracking, timeZone: "UTC" } }) : "-";

    const nameColumn: LastElementTableColumns = {
        id: "name",
        label: t("BeneficiarInfo.Name"),
        align: "left",
        // Planche 1: the name column is the widest one
        minWidth: hasTracking ? "180px" : undefined,
        format: (value: any, id?: number, currRow?: any) => {
            if (currRow?.missing) return muted(value, true);
            const document = documentList.find((doc) => doc.id === id);
            return (
                <div className="mui-cell" onClick={() => download(document)}>
                    <Pdf />
                    {value}
                </div>
            );
        },
        sort: true,
        avatar: false,
    };
    const uploadedByColumn: LastElementTableColumns = {
        id: "uploadedBy",
        label: t(hasTracking ? "BeneficiaryDocuments.Documents.AddedBy" : "BeneficiarInfo.UploadedBy"),
        align: hasTracking ? "center" : "left",
        format: (value: any) =>
            hasTracking ? (
                <span className="documents-muted documents-break">{uploadedByLabel(value)}</span>
            ) : (
                uploadedByLabel(value)
            ),
        sort: true,
    };
    const addedAtColumn: LastElementTableColumns = {
        id: "addedAt",
        label: t(hasTracking ? "BeneficiaryDocuments.Documents.AddedAt" : "BeneficiarInfo.AddedAt"),
        align: hasTracking ? "center" : "left",
        format: (value: any) => (hasTracking ? muted(formatDate(value)) : formatDate(value)),
        sort: true,
    };

    const legacyColumns: LastElementTableColumns[] = [
        nameColumn,
        uploadedByColumn,
        addedAtColumn,
        {
            id: "size",
            label: t("BeneficiarInfo.Size"),
            align: "left",
            format: (value: any) => (value ? getFileSize(value) : "-"),
            sort: true,
        },
        {
            id: "",
            label: "",
            align: "center",
            format: (value: any, id?: number, currRow?: any) => (
                <div className="mui-cell" style={{ justifyContent: "center" }}>
                    <Basket onClick={() => handleShowDocumentDeleteModal(currRow)} />
                </div>
            ),
        },
    ];

    // Planche 1: name, type, validity, status, added by, added on, actions
    const trackingColumns: LastElementTableColumns[] = [
        nameColumn,
        {
            id: "documentTypeId",
            label: t("BeneficiaryDocuments.Documents.Type"),
            align: "center",
            sort: true,
            format: (value: any, id?: number, currRow?: any) => {
                const type = allDocumentTypes.find((documentType) => documentType.id === value);
                if (type) return type.name;

                return muted(
                    t(
                        currRow?.status === "pending"
                            ? "BeneficiaryDocuments.Documents.ToQualify"
                            : "BeneficiaryDocuments.Documents.OtherDocument",
                    ),
                    true,
                );
            },
        },
        {
            id: "validUntil",
            label: t("BeneficiaryDocuments.Documents.Validity"),
            align: "center",
            sort: true,
            format: (value: any, id?: number, currRow?: any) =>
                muted(
                    currRow?.validFrom || value
                        ? `${currRow?.validFrom ? formatDate(currRow.validFrom) : "…"} → ${
                              value ? formatDate(value) : "…"
                          }`
                        : "—",
                ),
        },
        {
            id: "status",
            label: t("BeneficiaryDocuments.Documents.Status"),
            align: "center",
            format: (value: any, id?: number, currRow?: any) => {
                const status = currRow?.missing ? "missing" : getDocumentRowStatus(currRow, documentList);
                const reason: string = currRow?.rejectionReason ?? "";
                const shortReason =
                    reason.length > REJECTION_REASON_MAX_LENGTH
                        ? `${reason.slice(0, REJECTION_REASON_MAX_LENGTH).trim()}…`
                        : reason;

                return (
                    <div className="documents-cell documents-cell-center">
                        <StatusChip
                            status={status}
                            statusRules={statusRules}
                            tooltipText={reason}
                            extraText={status === "rejected" && reason ? `· ${shortReason}` : undefined}
                        />
                    </div>
                );
            },
        },
        uploadedByColumn,
        addedAtColumn,
        {
            id: "",
            label: "",
            align: "right",
            format: (value: any, id?: number, currRow?: any) =>
                currRow?.missing ? (
                    <div className="documents-cell documents-cell-right">
                        {canAdd && (
                            <StyledButton buttonType="info" onClick={() => openAddModal(currRow.documentTypeId)}>
                                {t("BeneficiaryDocuments.Documents.Add")}
                            </StyledButton>
                        )}
                    </div>
                ) : currRow?.status === "pending" && canQualify ? (
                    <div className="documents-cell documents-cell-right">
                        <StyledButton buttonType="danger" onClick={() => setDocumentToReject(currRow)}>
                            {t("BeneficiaryDocuments.Documents.Reject")}
                        </StyledButton>
                        <StyledButton buttonType="info" onClick={() => setDocumentToQualify(currRow)}>
                            {t("BeneficiaryDocuments.Documents.Qualify")}
                        </StyledButton>
                    </div>
                ) : (
                    <div className="documents-cell documents-cell-right">
                        <button
                            type="button"
                            className="documents-icon-button"
                            aria-label={t("BeneficiaryDocuments.Documents.Download")}
                            onClick={() => download(currRow)}
                        >
                            <ActionDownload />
                        </button>
                        {((canQualify && !!currRow?.documentTypeId) || canDelete) && (
                            <ThreeDotMenu
                                style={{ border: "none", color: "#646464", width: 30, height: 30 }}
                                menuIcon={<MoreVertIcon fontSize="small" />}
                                options={[
                                    {
                                        id: "edit",
                                        name: t("BeneficiaryDocuments.Documents.EditValidity"),
                                        hide: !currRow?.documentTypeId || !canQualify,
                                        action: () => setDocumentToQualify(currRow),
                                    },
                                    {
                                        id: "delete",
                                        name: t("BeneficiaryDocuments.Documents.Delete"),
                                        hide: !canDelete,
                                        action: () => handleShowDocumentDeleteModal(currRow),
                                    },
                                ]}
                            />
                        )}
                    </div>
                ),
        },
    ];

    return (
        <div className="documents">
            <AddDocument
                isModalOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onFileAdd={handleFileAdd}
                documentTypes={documentTypes}
                modalSubtitle={beneficiaryName}
                beneficiary={isOwnerLoaded ? beneficiar : undefined}
                documents={hasTracking ? documentList : undefined}
                defaultDocumentTypeId={addDocumentTypeId}
            />
            {canRemind && (
                <SendEmailBeneficiar
                    isModalOpen={isReminderOpen}
                    onClose={() => setIsReminderOpen(false)}
                    beneficiaries={[toTermBeneficiary(beneficiar)]}
                    hasCustomStatementsBeneficiars={[]}
                    haveNoEmail={beneficiar.email ? [] : [toTermBeneficiary(beneficiar)]}
                    periodId={undefined}
                    isBulk={false}
                    documentsReminder={{ onSend: handleSendReminder, preview: reminderPreview }}
                />
            )}
            {hasTracking && documentToQualify && (
                <QualifyDocumentModal
                    key={documentToQualify.id}
                    document={documentToQualify}
                    documentTypes={typesForQualify(documentToQualify)}
                    beneficiary={isOwnerLoaded ? beneficiar : undefined}
                    onClose={() => setDocumentToQualify(undefined)}
                    onSave={handleQualify}
                />
            )}
            {hasTracking && documentToReject && (
                <RejectDocumentModal
                    key={documentToReject.id}
                    document={documentToReject}
                    onClose={() => setDocumentToReject(undefined)}
                    onReject={handleReject}
                />
            )}
            <DeleteModal
                elementTitle={`"${documentToDelete?.name}"` || ""}
                open={isDeleteModalOpen}
                onDelete={handleDeleteDocument}
                onClose={() => handleDeleteDocument(false)}
            />
            {hasSubtitles && (
                <h3 style={{ fontSize: "14px", fontWeight: 600, color: variables["foreground"] }}>Documents</h3>
            )}
            <div className="documents-filters">
                <div className="documents-filters-search-bar">
                    <FilterInput filterValue={filterValue} setFilterValue={setFilterValue} fitBtnSize />
                </div>
                {canRemind && (
                    <StyledButton onClick={() => setIsReminderOpen(true)} buttonType="secondary">
                        <SendEmailIcon />
                        {t("BeneficiaryDocuments.Table.Remind")}
                    </StyledButton>
                )}
                {canAdd && (
                    <StyledButton onClick={() => openAddModal()} buttonType="outlined">
                        <Plus />
                        {t("BeneficiarInfo.AddDocument")}
                    </StyledButton>
                )}
            </div>
            {rows.length == 0 && (
                <div className="documents-documents-empty-state">
                    <DocumentEmptyState />
                    <div className="documents-documents-empty-state-text">{t("BeneficiarInfo.NoDocument")}</div>
                    <div className="documents-documents-empty-state-subtext">
                        {t("BeneficiarInfo.NoDocumentText")}
                        <br />
                        {t("BeneficiarInfo.NoDocumentSubText")}
                    </div>
                </div>
            )}
            {filteredArray.length > 0 && !hasTracking && (
                <LastElementTable
                    columns={legacyColumns}
                    data={filteredArray}
                    max={-1}
                    stickyFooter
                    dataType="documents"
                />
            )}
            {rows.length > 0 && hasTracking && (
                <LastElementTable
                    columns={trackingColumns}
                    data={rows}
                    max={rows.length}
                    dataType="documents"
                    rowClassName={(row) => (row.status === "pending" ? "documents-row-pending" : undefined)}
                    bottomFooterContent={
                        <div className="documents-summary">
                            {t("BeneficiaryDocuments.Documents.SummaryTotal", { count: filteredArray.length })}
                            {missingRows.length > 0 &&
                                ` · ${t("BeneficiaryDocuments.Documents.SummaryMissing", {
                                    count: missingRows.length,
                                })}`}
                            {pendingCount > 0 &&
                                ` · ${t("BeneficiaryDocuments.Documents.SummaryPending", { pending: pendingCount })}`}
                        </div>
                    }
                />
            )}
        </div>
    );
};

export default Documents;
